import React, { forwardRef, useImperativeHandle } from 'react';
import { useMutation } from 'hooks/apollo';
import { Form, Tabs } from 'antd';

import { FormattedMessage } from 'react-intl';
import EditQuestion from './EditQuestion';
import EditBlock from './EditBlock';
import EditText from './EditText';

import createBlockMutation from './createBlock.gql';
import updateBlockMutation from './updateBlock.gql';
import createQuestionMutation from './createQuestion.gql';
import updateQuestionMutation from './updateQuestion.gql';
import createTextMutation from './createText.gql';
import updateTextMutation from './updateText.gql';

import messages from './messages';
import EditQuestionGroup from './EditQuestionGroup';

function toOptionInput(option, index) {
  return {
    id: option.id,
    title: option.title,
    value: option.value,
    active: option.active,
    position: index,
  };
}

function toCreateQuestionInput(position, surveyId, parentId, type, values) {
  const { questionTags, resultsCollection } = values;
  const categoryIds = questionTags.concat(resultsCollection);
  const options = values.options?.map(toOptionInput);

  return {
    position,
    surveyId,
    parentId,
    type,
    categoryIds,
    options,
    title: values.title,
    description: values.description,
    dependencies: values.dependencies.filter(d => d.questionId),
    dependencyOperator: values.dependencyOperator,
    settings: {
      subType: values.subType,
      required: values.required,
      random: values.random,
      lockLastOption: values.lockLastOption,
      min: values.min,
      max: values.max,
      email: values.email,
      rows: values.rows,
    },
  };
}

function toUpdateQuestionInput(id, values) {
  const { questionTags, resultsCollection } = values;
  const categoryIds = questionTags.concat(resultsCollection).filter(x => x);
  const options = values.options?.map(toOptionInput);

  return {
    id,
    categoryIds,
    options,
    title: values.title,
    description: values.description,
    dependencies: values.dependencies.filter(d => d.questionId),
    dependencyOperator: values.dependencyOperator,
    settings: {
      subType: values.subType,
      required: values.required,
      random: values.random,
      lockLastOption: values.lockLastOption,
      min: values.min,
      max: values.max,
      email: values.email,
      rows: values.rows,
    },
  };
}

function getDependencies(dependencies = []) {
  return dependencies.map(d => ({
    id: d.id,
    questionId: d.question?.id,
    optionIds: d.options?.map(o => o.id),
    answer: d.answer,
  }));
}

const EditContentForm = (props, ref) => {
  const { content, surveyQuestions, onDone, surveyId } = props;
  const { id: contentId, parentId, position, block, question, text, dependencyOperator } = content || {};
  const dependencies = getDependencies(content.dependencies);

  const [editBlockForm] = Form.useForm();
  const [editQuestionForm] = Form.useForm();
  const [editTextForm] = Form.useForm();
  const [questionGroupForm] = Form.useForm();

  const [activeTab, setActiveTab] = React.useState('details');

  // The component instance will be extended
  // with whatever you return from the callback passed
  // as the second argument
  useImperativeHandle(ref, () => ({
    // Return false if form is invalid.
    save: async () => {
      let form = null;
      if (block) {
        form = editBlockForm;
      } else if (question) {
        if (activeTab === 'group') {
          form = questionGroupForm;
        } else {
          form = editQuestionForm;
        }
      } else if (text) {
        form = editTextForm;
      }
      if (!form) {
        console.error('Unsupported content type');
        return false;
      }
      try {
        await form.validateFields();
        form.submit();
      } catch (validationError) {
        return false;
      }
      return true;
    },
    cancel: () => {
      let form = null;
      if (block) {
        form = editBlockForm;
      } else if (question) {
        form = editQuestionForm;
      } else if (text) {
        form = editTextForm;
      }

      if (form) {
        form.resetFields();
      } else {
        console.error('Unsupported content type');
      }
    },
  }), [content, activeTab]);

  const [createBlock] = useMutation(createBlockMutation);
  const [updateBlock] = useMutation(updateBlockMutation);
  const upsertBlock = async (values) => {
    let error = null;
    try {
      if (!contentId) {
        await createBlock({ variables: { parentId, position, ...values } });
      } else {
        await updateBlock({ variables: { contentId, ...values } });
      }
    } catch (e) {
      error = e;
    }
    onDone(error);
  };

  const [createQuestion] = useMutation(createQuestionMutation);
  const [updateQuestion] = useMutation(updateQuestionMutation);
  const upsertQuestion = async (values) => {
    let error = null;
    try {
      if (!contentId) {
        const input = toCreateQuestionInput(position, surveyId, parentId, question.settings.type, values);
        await createQuestion({ variables: { input } });
      } else {
        const input = toUpdateQuestionInput(question.id, values);
        await updateQuestion({ variables: { input } });
      }
    } catch (e) {
      error = e;
    }
    onDone(error);
  };
  const [createText] = useMutation(createTextMutation);
  const [updateText] = useMutation(updateTextMutation);
  const upsertText = async (values) => {
    let error = null;

    try {
      if (!contentId) {
        await createText({ variables: { input: { parentId, position, ...values } } });
      } else {
        await updateText({ variables: { input: { contentId, ...values } } });
      }
    } catch (e) {
      error = e;
    }
    onDone(error);
  };

  if (block) {
    return (
      <EditBlock
        block={block}
        dependencies={dependencies}
        dependencyOperator={dependencyOperator}
        surveyQuestions={surveyQuestions}
        onSubmit={upsertBlock}
        form={editBlockForm}
      />
    );
  }
  if (question) {
    return (
      <Tabs
        activeKey={activeTab}
        onChange={activeKey => setActiveTab(activeKey)}
        style={{ marginTop: -24 }}
        items={[{ key: 'details',
          label: <FormattedMessage {...messages.questionDetails} />,
          children: <EditQuestion
            question={question}
            dependencies={dependencies}
            dependencyOperator={dependencyOperator}
            surveyQuestions={surveyQuestions}
            onSubmit={upsertQuestion}
            form={editQuestionForm}
          /> }, { key: 'group',
          label: <FormattedMessage {...messages.questionGroup} />,
          children: <EditQuestionGroup
            question={question}
            surveyId={surveyId}
            onSubmit={onDone}
            form={questionGroupForm}
          /> }]}
      />
    );
  }
  if (text) {
    return (
      <EditText
        text={text}
        dependencies={dependencies}
        dependencyOperator={dependencyOperator}
        surveyQuestions={surveyQuestions}
        onSubmit={upsertText}
        form={editTextForm}
      />
    );
  }

  return 'Unknown Type 😮';
};

// Our current version of react-intl does not support forwardRef
export default forwardRef(EditContentForm);
